<template>
  <div class="grow h-full pb-2 relative">
    <ContentEditMediaPreview
      v-if="uploadedFileUrl && uploadedFileType"
      :url="uploadedFileUrl"
      :media-type="uploadedFileType"
      @cancel="resetUpload"
      @accept="handleMediaReady"
    />
    <template v-else>
      <!-- @vue-ignore -->
      <FileComponent
        ref="fileUploader"
        v-slot="{ selectFile, isOverDropZone, uploader }"
        show-button
        :upload-type="currentUploadType"
        class="absolute! inset-0"
        button-class="opacity-0"
        enable-cropper
        :cropper-sizes="cropperConfig.sizes"
        :multiple="false"
        :max-file-size="currentMaxFileSize"
        :mime-types="mediaMimeTypes"
        @upload-success="onUploadSuccess"
      >
        <div
          class="flex flex-col items-center justify-center h-full border-2 rounded-lg border-dashed relative"
          :class="isOverDropZone ? 'border-primary' : ''"
        >
          <template v-if="uploader.isUploading.value">
            <SpinIcon />
            <p class="font-medium">
              Uploading {{ (uploader.uploadProgress.value ?? 0) * 100 }}%...
            </p>
          </template>
          <template v-else>
            <i class="pi pi-image text-2xl" />
            <p class="mt-2">Drag and drop your image or video here</p>
            <p class="mb-2">or</p>
            <button
              class="relative cursor-pointer btn bg-primary text-surface-0 hover:bg-primary-600 active:bg-primary-700"
              @click="selectFile()"
            >
              Select File
            </button>
            <p class="text-xs ma-text-secondary mt-2">
              Images: Max {{ maxImageSize / MEGABYTE }}MB | Videos: Max
              {{ maxVideoSize / MEGABYTE }}MB
            </p>
            <p v-if="uploader.error.value" class="text-red-500 text-sm mt-2">
              {{ uploader.error.value }}
            </p>
          </template>
        </div>
      </FileComponent>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import SpinIcon from "@/components/icons/SpinIcon.vue";
import FileComponent from "@/components/upload/FileComponent.vue";
import ContentEditMediaPreview from "./attachments/ContentEditMediaPreview.vue";
import { MEGABYTE } from "@/utils/consts";
import { getFileMimeTypesForExtensions } from "@/utils/files";
import type { FileCategory } from "@/types/files";
import { getPlatformImageSpec, type SupportedPlatform } from "@/utils/platformImageSpecs";

interface Props {
  maxImageSize?: number;
  maxVideoSize?: number;
  platform?: SupportedPlatform;
}

const props = withDefaults(defineProps<Props>(), {
  maxImageSize: 5 * MEGABYTE,
  maxVideoSize: 195 * MEGABYTE,
});

const emit = defineEmits<{
  imageSelected: [url: string];
  videoSelected: [url: string];
}>();

// MIME type constants
const imageMimeTypes = getFileMimeTypesForExtensions(["jpg", "jpeg", "png", "webp"]);
const videoMimeTypes = getFileMimeTypesForExtensions(["mp4", "webm", "mov"]);
const mediaMimeTypes = [...imageMimeTypes, ...videoMimeTypes];

const uploadedFileUrl = ref("");
const uploadedFileType = ref<"image" | "video" | "">("");
const currentUploadType = ref<FileCategory>("task-image");
const currentMaxFileSize = ref(props.maxImageSize);

const cropperConfig = computed(() => getPlatformImageSpec(props.platform));

const queryClient = useQueryClient();

function onUploadSuccess(data: ReturnType<typeof uploadSingleFile>) {
  const state = unref(data.state);
  if (!state) return;
  uploadedFileUrl.value = enforceFilesDomain(state.data?.path) || "";

  // Determine the file type based on content type
  const contentType = state.data?.contentType || "";
  if (contentType.startsWith("image/")) {
    uploadedFileType.value = "image";
    currentUploadType.value = "task-image";
    currentMaxFileSize.value = props.maxImageSize;
  } else if (contentType.startsWith("video/")) {
    uploadedFileType.value = "video";
    currentUploadType.value = "task-video";
    currentMaxFileSize.value = props.maxVideoSize;
  } else {
    resetUpload();
  }

  queryClient.invalidateQueries({
    queryKey: ["files", "list"],
  });
}

function handleMediaReady() {
  if (!uploadedFileUrl.value) return;

  if (uploadedFileType.value === "image") {
    emit("imageSelected", uploadedFileUrl.value);
  } else if (uploadedFileType.value === "video") {
    emit("videoSelected", uploadedFileUrl.value);
  }

  resetUpload();
}

function resetUpload() {
  if (uploadedFileUrl.value) {
    URL.revokeObjectURL(uploadedFileUrl.value);
  }
  uploadedFileUrl.value = "";
  uploadedFileType.value = "";
  currentUploadType.value = "task-image";
  currentMaxFileSize.value = props.maxImageSize;
}
</script>
